import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import { toast } from "react-toastify";
import {
  getTestSeriesDetail,
  addTestToSeries,
  removeTestFromSeries,
  publishTestSeries,
  reorderSeriesTests,
  apiErrorMessage,
} from "../../api/assessmentTestSeries";
import { listTests } from "../../api/assessmentTests";

const ACCENT = "#17479e";
const PAGE_SIZE = 100;

// Every PUBLISHED test of the course, across pages (the backend only lets
// published tests of the series' own course into a series).
async function fetchAllPublishedTests(courseId) {
  const rows = [];
  for (let page = 0; ; page += 1) {
    const data = await listTests({ courseId, status: "PUBLISHED", page, size: PAGE_SIZE });
    rows.push(...(data.content || []));
    if (!data.hasNext) break;
  }
  return rows;
}

function TestSeriesTests({ open, series, onClose, onChanged }) {
  const [tests, setTests] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const editable = series?.status === "DRAFT";

  const load = () => {
    if (!series) return Promise.resolve();
    setLoading(true);
    return getTestSeriesDetail(series.id)
      .then((detail) => setTests(detail?.tests || []))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load series tests")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !series) return;
    setPick(null);
    setTests([]);
    setAvailableTests([]);
    load();
    if (series.status === "DRAFT") {
      fetchAllPublishedTests(series.courseId)
        .then(setAvailableTests)
        .catch((err) => toast.error(apiErrorMessage(err, "Failed to load available tests")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, series]);

  const memberIds = useMemo(() => new Set(tests.map((t) => t.testId)), [tests]);
  const addable = useMemo(
    () => availableTests.filter((t) => !memberIds.has(t.id)),
    [availableTests, memberIds],
  );

  const run = (action, fallback) => {
    setBusy(true);
    return action()
      .then(() => {
        onChanged?.();
        return load();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, fallback));
        return load();
      })
      .finally(() => setBusy(false));
  };

  const handleAdd = () => {
    if (!pick) return;
    run(() => addTestToSeries(series.id, pick.id), "Failed to add test").then(() => setPick(null));
  };

  const handlePublish = () => {
    setPublishing(true);
    publishTestSeries(series.id)
      .then(() => {
        toast.success("Test series published!");
        setConfirmPublish(false);
        onChanged?.();
        onClose();
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to publish test series")))
      .finally(() => setPublishing(false));
  };

  const handleRemove = (testId) => run(() => removeTestFromSeries(series.id, testId), "Failed to remove test");

  const handleMove = (index, delta) => {
    const next = [...tests];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setTests(next);
    run(() => reorderSeriesTests(series.id, next.map((t) => t.testId)), "Failed to reorder tests");
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <PlaylistAddCheckOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Manage Tests</Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>{series?.name}</Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        {!editable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This series is {series?.status?.toLowerCase()}. Tests can only be added, removed or reordered while a series
            is a draft.
          </Alert>
        )}

        {editable && (
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={addable}
              value={pick}
              onChange={(_, value) => setPick(value)}
              getOptionLabel={(t) => t.name || ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="No published tests of this exam left to add"
              renderInput={(params) => <TextField {...params} label="Add a published test" />}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!pick || busy}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap", height: 40 }}
            >
              Add
            </Button>
          </Stack>
        )}

        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 0.5 }}>
          Tests in this series ({tests.length})
        </Typography>
        <Divider />
        {loading ? (
          <LinearProgress sx={{ mt: 2 }} />
        ) : tests.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No tests in this series yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {tests.map((t, index) => (
              <ListItem
                key={t.testId}
                divider
                secondaryAction={
                  editable && (
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Move up" arrow placement="top">
                        <span>
                          <IconButton size="small" disabled={busy || index === 0} onClick={() => handleMove(index, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down" arrow placement="top">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy || index === tests.length - 1}
                            onClick={() => handleMove(index, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove from series" arrow placement="top">
                        <span>
                          <IconButton size="small" color="error" disabled={busy} onClick={() => handleRemove(t.testId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  )
                }
              >
                <ListItemText
                  primary={`${index + 1}. ${t.testName}`}
                  primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: "0.85rem" } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        {editable && (
          <Button
            variant="contained"
            color="success"
            disableElevation
            disabled={busy || tests.length === 0}
            onClick={() => setConfirmPublish(true)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, mr: "auto" }}
          >
            Publish Series
          </Button>
        )}
        <LoadingButton
          onClick={onClose}
          disabled={busy}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            color: "#555",
            backgroundColor: "#eceff1",
            "&:hover": { backgroundColor: "#e0e3e6" },
          }}
        >
          Close
        </LoadingButton>
      </DialogActions>

      <Dialog open={confirmPublish} onClose={() => !publishing && setConfirmPublish(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 1 }}>Publish test series?</Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
            Publishing &ldquo;{series?.name}&rdquo; makes it available to students. After that, its name, description, access
            settings (free or premium, price and duration) and list of tests can no longer be changed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmPublish(false)} disabled={publishing}>
            Cancel
          </Button>
          <LoadingButton variant="contained" color="success" loading={publishing} onClick={handlePublish}>
            Publish
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

TestSeriesTests.propTypes = {
  open: PropTypes.bool.isRequired,
  series: PropTypes.shape({
    id: PropTypes.number,
    courseId: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

export default TestSeriesTests;
